import React, { useCallback, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useL10n } from '@uplift/hooks';
import { Button, Loader } from '@uplift/ui-v2';
import { WalletButton, WalletPaymentResult } from '../../../../../components';
import { CardDetailsForm } from './card-details-form.form';
import { BillingAddressForm } from './billing-address-form.form';

export type PaymentMethodFormProps = {
  isMobile?: boolean;
  onWalletPayment: (result: WalletPaymentResult) => Promise<void>;
};

// Wraps CardDetailsForm + BillingAddressForm so neither renders until we know whether a
// wallet is available: wallet button on top with an "Add Payment Method" fallback when a
// wallet exists, straight to the card form when it doesn't.
export const PaymentMethodForm: React.VFC<PaymentMethodFormProps> = ({ isMobile = false, onWalletPayment }) => {
  const l10n = useL10n();
  const [ walletChecked, setWalletChecked ] = useState(false);
  const [ walletAvailable, setWalletAvailable ] = useState(false);
  const [ showCardForm, setShowCardForm ] = useState(false);

  const handleWalletAvailabilityChange = useCallback((available: boolean) => {
    setWalletAvailable(available);
    setWalletChecked(true);
  }, []);

  // No wallet to offer: the card form is the only option, so skip the "Add Payment Method" toggle.
  const displayCardForm = showCardForm || !walletAvailable;

  return (
    <Box display="flex" flexDirection="column">
      {/* The wallet check runs here regardless of what's visible yet — WalletButton hides
          itself internally until it knows whether a wallet is usable. */}
      <Box mt={4} px={{ xs: 2, sm: 3 }} pb={2} sx={{ display: walletChecked && walletAvailable ? 'block' : 'none' }}>
        <WalletButton onWalletPayment={onWalletPayment} onAvailabilityChange={handleWalletAvailabilityChange} />
      </Box>

      {!walletChecked && (
        <Box mt={4} px={{ xs: 2, sm: 3 }} pb={3} display="flex" justifyContent="center">
          <Loader height="120px" />
        </Box>
      )}

      {walletChecked && walletAvailable && (
        <Box px={{ xs: 2, sm: 3 }} pb={2} display="flex" alignItems="center" gap={2}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="textSecondary">
            {showCardForm ? l10n.accountSettings.billing.orEnterCardDetails : 'OR'}
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>
      )}

      {walletChecked && walletAvailable && !showCardForm && (
        <Box px={{ xs: 2, sm: 3 }} pb={2}>
          <Button variant="outlined" color="secondary" fullWidth onClick={() => setShowCardForm(true)}>
            {l10n.accountSettings.billing.addPaymentMethod}
          </Button>
        </Box>
      )}

      {walletChecked && displayCardForm && (
        <>
          <CardDetailsForm isMobile={isMobile} />
          <BillingAddressForm />
        </>
      )}
    </Box>
  );
};
